import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T = unknown> {
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
}

export const useAutoSave = <T = unknown>({ onSave, debounceMs = 3000, enabled = true }: UseAutoSaveOptions<T>) => {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const performSave = useCallback(
    async (data: T) => {
      if (!enabled) return;

      setStatus('saving');
      
      try {
        await onSave(data);
        setStatus('saved');
        setLastSavedAt(new Date());
        
        // Reset to idle after showing saved state
        if (statusTimeoutRef.current) {
          clearTimeout(statusTimeoutRef.current);
        }
        statusTimeoutRef.current = setTimeout(() => {
          setStatus('idle');
        }, 2000);
      } catch (error) {
        console.error('Auto-save failed:', error);
        setStatus('error');
      }
    },
    [onSave, enabled]
  );

  const triggerAutoSave = useCallback(
    (data: T) => {
      if (!enabled) return;

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout
      saveTimeoutRef.current = setTimeout(() => {
        performSave(data);
      }, debounceMs);
    },
    [performSave, debounceMs, enabled]
  );

  const saveImmediately = useCallback(
    (data: T) => {
      if (!enabled) return;

      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Save immediately
      performSave(data);
    },
    [performSave, enabled]
  );

  return {
    triggerAutoSave,
    saveImmediately,
    status,
    lastSavedAt,
  };
};